import React, { useEffect, useState } from 'react';
import { ROLES, SxStyle } from '../../../types';
import { useStringsContext } from '../../../contexts/strings';
import AppMenuItems from './app-menu-items';
import { useFormWithValidation } from '../../../contexts/formWithValidation';
import { doc, updateDoc } from 'firebase/firestore';
import firebaseApp from '../../../firebase-app';
import { getAppConfigPath } from '../../../utils/paths';
import { AppConfigBottomBarItem, BottomBarItem, EditableBottomBarItem } from './types';
import { useSnackbar } from 'notistack';
import ScenePage from '../../common/scene/scene-page';
import AppMenuHeader from './app-menu-header';
import Box from '@mui/material/Box';
import { useConfig } from '../../../contexts/config';
import { defaultStandardItems } from './items';
import { useUserAuth } from '../../../contexts/auth';

const styles: SxStyle = {
  container: (theme) => ({
    marginTop: theme.spacing(4),
  }),
};

interface Props {
  selectedAbbrv: string;
}

const getRandomId = () => Math.floor(Math.random() * (100000 - 10000) + 10000);

const AppMenu = ({ selectedAbbrv }: Props) => {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { addFormData, formData, resetDirty, isDirty, isValid } =
    useFormWithValidation<EditableBottomBarItem[]>();

  const { appConfig } = useConfig();

  const { isSuperAdmin, hasTeamRole } = useUserAuth();
  const isReadOnly = !isSuperAdmin && hasTeamRole(selectedAbbrv, ROLES.VIEWER);

  useEffect(() => {
    let bottomBarItems: EditableBottomBarItem[] = [];
    if (appConfig.bottomBar?.items) {
      bottomBarItems = appConfig.bottomBar.items.map(
        (appConfigMenuItem: AppConfigBottomBarItem) => {
          const bottomBarItem =
            appConfigMenuItem.type === 'customUrl'
              ? (appConfigMenuItem as BottomBarItem)
              : defaultStandardItems[appConfigMenuItem.type];
          return {
            ...bottomBarItem,
            id: getRandomId(),
            draggable: true,
            editable: appConfigMenuItem.type !== 'unibet',
          } satisfies EditableBottomBarItem;
        }
      );
    }
    addFormData(bottomBarItems);
  }, [appConfig, addFormData]);

  const onSubmit = async () => {
    if (!appConfig) return;

    setIsSubmitting(true);

    const updateBottomBarItems: AppConfigBottomBarItem[] = formData.map(
      ({ type, text, url, icon }) => {
        return (
          type === 'customUrl'
            ? {
                type,
                text,
                icon,
                url,
              }
            : {
                type,
              }
        ) satisfies AppConfigBottomBarItem;
      }
    );

    try {
      const docRef = doc(firebaseApp.db, getAppConfigPath(selectedAbbrv));
      await updateDoc(docRef, {
        bottomBar: {
          ...appConfig.bottomBar,
          items: updateBottomBarItems,
        },
      });

      resetDirty();

      enqueueSnackbar(strings.messages.saveSuccess, {
        variant: 'success',
      });
    } catch (error) {
      console.error(error);

      enqueueSnackbar(strings.messages.saveError, {
        variant: 'error',
      });
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <ScenePage title={strings.appBar.appMenu}>
        <AppMenuHeader
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          isDisabled={!isDirty || !isValid || isReadOnly}
        />
        <Box sx={styles.container}>
          <AppMenuItems />
        </Box>
      </ScenePage>
    </>
  );
};

export default AppMenu;
